import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MdPerson, MdLogout, MdKeyboardArrowDown, MdSecurity } from 'react-icons/md';

/* ── Reusable dropdown nav item ──────────────────────────────────── */
function NavDropdown({ label, items, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const timeoutRef = useRef(null);

  const anyActive = items.some((i) => location.pathname === i.path);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        onClick={() => setOpen((p) => !p)}
        className={`flex items-center gap-1 px-4 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
          anyActive
            ? 'bg-primary text-white'
            : 'text-gray-800 hover:bg-gray-100'
        }`}
        data-testid={testId}
      >
        {label}
        <MdKeyboardArrowDown
          className={`text-base transition-transform ${open ? 'rotate-180' : ''} ${anyActive ? 'text-white' : 'text-gray-400'}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-card shadow-lg border border-gray-200 py-1 z-50">
          {items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setOpen(false)}
              className={`block px-4 py-2 text-sm font-rubik transition-colors ${
                location.pathname === item.path
                  ? 'bg-primary/10 text-primary font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={item.testId}
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Simple single-link nav item ─────────────────────────────────── */
function NavLink({ to, label, testId }) {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-card text-sm font-medium font-rubik transition-all ${
        active ? 'bg-primary text-white' : 'text-gray-800 hover:bg-gray-100'
      }`}
      data-testid={testId}
    >
      {label}
    </Link>
  );
}

/* ── Main navbar ─────────────────────────────────────────────────── */
function AdminNavBar() {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { user, logout } = useAuth();
  const menuRef = useRef(null);

  const handleClickOutside = useCallback((event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setShowUserMenu(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <Link to="/admin/dashboard" data-testid="admin-nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png"
              alt="CIVOTE Admin"
              className="h-10 w-10 rounded-lg"
            />
          </Link>
        </div>

        {/* Navigation */}
        <div className="flex items-center space-x-1">
          <NavLink to="/admin/dashboard" label="Dashboard" testId="admin-nav-dashboard" />

          <NavDropdown
            label="Polls"
            testId="admin-nav-polls"
            items={[
              { path: '/admin/polls', label: 'System Polls', testId: 'admin-nav-system-polls' },
              { path: '/admin/custom-polls', label: 'Custom Polls', testId: 'admin-nav-custom-polls' },
            ]}
          />

          <NavLink to="/admin/tags" label="Tags" testId="admin-nav-tags" />
          <NavLink to="/admin/users" label="Users" testId="admin-nav-users" />

          <NavDropdown
            label="Flagged"
            testId="admin-nav-flagged"
            items={[
              { path: '/admin/flagged-polls', label: 'Flagged Polls', testId: 'admin-nav-flagged-polls' },
              { path: '/admin/flagged-reps', label: 'Flagged Reps', testId: 'admin-nav-flagged-reps' },
            ]}
          />

          <NavLink to="/admin/offices" label="Offices" testId="admin-nav-offices" />
          <NavLink to="/admin/poll-ratings" label="Ratings" testId="admin-nav-poll-ratings" />

          <NavDropdown
            label="Pricing"
            testId="admin-nav-pricing"
            items={[
              { path: '/admin/poll-pricing', label: 'Poll Pricing', testId: 'admin-nav-poll-pricing' },
              { path: '/admin/assessment-pricing', label: 'Assessment Pricing', testId: 'admin-nav-assessment-pricing' },
              { path: '/admin/subscriptions', label: 'Subscriptions', testId: 'admin-nav-subscriptions' },
              { path: '/admin/promo-codes', label: 'Promo Codes', testId: 'admin-nav-promo-codes' },
              { path: '/admin/revenue', label: 'Revenue', testId: 'admin-nav-revenue' },
            ]}
          />

          <NavDropdown
            label="System"
            testId="admin-nav-system"
            items={[
              { path: '/admin/api-usage', label: 'API Usage', testId: 'admin-nav-api-usage' },
              { path: '/admin/redis-health', label: 'Redis Health', testId: 'admin-nav-redis-health' },
              { path: '/admin/rss-health', label: 'RSS Health', testId: 'admin-nav-rss-health' },
            ]}
          />

          <NavLink to="/admin/verifications" label="Verify" testId="admin-nav-verifications" />
        </div>

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-card bg-gray-100 hover:bg-gray-200 transition-all"
            data-testid="admin-user-menu-button"
          >
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
              <MdPerson className="text-white text-lg" />
            </div>
            <MdKeyboardArrowDown className={`text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg border border-gray-200 py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-200">
                <p className="text-sm font-medium text-gray-800 font-rubik">{user?.username || 'Admin'}</p>
                <p className="text-xs text-gray-500 font-rubik">Administrator</p>
              </div>
              <Link
                to="/admin/profile"
                onClick={() => setShowUserMenu(false)}
                className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors font-rubik text-sm"
                data-testid="admin-user-menu-profile"
              >
                <MdSecurity className="text-lg" />
                Profile & Security
              </Link>
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                }}
                className="w-full flex items-center gap-3 px-4 py-2 text-danger hover:bg-red-50 transition-colors font-rubik text-sm"
                data-testid="admin-user-menu-logout"
              >
                <MdLogout className="text-lg" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

export default AdminNavBar;
